// Shared helpers for Gambio lister tab views (extracted from the former list-mapping.ts).
export async function callFn<T>(
  ctx: { supabaseUrl: string; session: { token: string } },
  name: string,
  body: unknown,
): Promise<T> {
  const res = await fetch(`${ctx.supabaseUrl}/functions/v1/${name}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${ctx.session.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const t = await res.text().catch(() => '');
    throw new Error(`${name} ${res.status}: ${t.slice(0, 200)}`);
  }
  return (await res.json()) as T;
}

/** Eine flache, im vario_path-Dropdown wählbare Vario-Quelle. `path` ist der beim Speichern
 *  abgelegte Wert (bare Pfad, vom Worker/Preview via resolveVarioPath aufgelöst - Listing zuerst,
 *  dann Artikel). */
export interface FlatSource { path: string; label: string; group: string; example: string | null; }

interface RawSource { sourceKey: string; path: string; label: string; isVariantAttribute?: boolean; disabled?: boolean; exampleValue?: string | number | null }
interface RawSourceGroup { key: string; label: string; sources: RawSource[] }

/**
 * Lädt den dynamischen Vario-Quellen-Katalog (gambio-mapping-sources) und flacht ihn für die
 * vario_path-Auswahl ab - dieselbe Quelle/Logik wie der Otto-Mapping-Editor. Ausgeblendet:
 * Varianten-Achsen (`isVariantAttribute`), die Spezial-Gruppe (fester Wert/Template sind eigene
 * Modi) und synthetische Platzhalter-Pfade (`(…)`, nicht auflösbar). `articleId` (optional) reichert
 * die Liste um Varianten-Achsen/Beispiele an; ohne ihn kommen die generischen Listing-/Artikel-/
 * EAV-Quellen (für den globalen Einstellungs-Editor). Best-effort: bei Fehler leere Liste.
 */
export async function loadMappingSources(
  ctx: { supabaseUrl: string; session: { token: string } },
  opts?: { storefront?: string; articleId?: string },
): Promise<FlatSource[]> {
  const body: Record<string, unknown> = { action: 'list' };
  if (opts?.storefront) body['storefront'] = opts.storefront;
  if (opts?.articleId) body['articleId'] = opts.articleId;
  let groups: RawSourceGroup[] = [];
  try {
    const res = await callFn<{ groups?: RawSourceGroup[] }>(ctx, 'gambio-mapping-sources', body);
    groups = res.groups ?? [];
  } catch {
    return [];
  }
  const out: FlatSource[] = [];
  for (const g of groups) {
    if (g.key === 'special' || g.key === 'variants') continue;
    for (const s of g.sources ?? []) {
      if (s.isVariantAttribute || s.disabled) continue;
      if (!s.path || s.path.startsWith('(')) continue; // synthetische Anzeige-Pfade nicht auflösbar
      const ex = s.exampleValue == null ? null : String(s.exampleValue);
      out.push({ path: s.path, label: `${g.label} · ${s.label}`, group: g.label, example: ex });
    }
  }
  return out;
}

export function banner(kind: 'warn' | 'error', msg: string): string {
  const cls = kind === 'error' ? 'v-banner-danger' : 'v-banner-warn';
  const icon = kind === 'error' ? 'fa-circle-xmark' : 'fa-triangle-exclamation';
  return `<div class="v-pa-md"><div class="v-banner ${cls}"><span class="v-icon fa-light ${icon}"></span><div>${escapeHtml(msg)}</div></div></div>`;
}

export function escapeHtml(s: string | null | undefined): string {
  if (s === null || s === undefined) return '';
  return String(s)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

export function escapeAttr(s: string): string {
  return escapeHtml(s);
}
